// Collection book (CHANGELOG 6.5): 80 catalog items × 5 rarities = 400 fields (60 MVP items,
// Rider, Hunter and the accessories since krug 5, F10). A field
// opens the first time the player owns that item in that rarity. Completing an item
// category, a set or a rarity column gives a cosmetic reward (title, badge or share
// card frame), never a combat bonus. Rewards are achievements, so each is given once.

import type { AchievementDef } from './content/achievements';
import { RARITY_NAME } from './content/items';
import { currentItemId } from './items/legacy';
import { MVP_ITEMS } from './items/mvpItems';
import { SETS } from './items/sets';
import { RARITIES, SET_IDS, type Rarity, type SetId, type StackKind, type WeaponClass } from './types';

export type CollectionGroupKind = 'category' | 'set' | 'rarity';

export interface CollectionGroup {
  id: string;
  kind: CollectionGroupKind;
  name: string;
  /** Item ids in the group (fields = items × rarities, or items for a rarity column). */
  items: string[];
  rarities: Rarity[];
  achievementId: string;
  /** Share card frame unlocked with a set. */
  frame?: SetId;
}

export interface CollectionState {
  /** "templateId:rarity" keys of every field found. */
  found: string[];
  /** Package 4, part D: named enemies beaten (the book's "Named" page). */
  named?: import('./named').NamedId[];
  /** Share card frames unlocked (set ids), newest last. */
  frames: string[];
  /**
   * B5 (1. 10. 2026): every kind of resource, consumable or special that ever came into Storage. A kind
   * gets the NEW mark only the first time (missing on survivors from before: filled at the server's start).
   */
  stacks?: StackKind[];
  /**
   * 4. 10. 2026 (package 3, C): outfits a season reward unlocked for the account (a champion's Legendary outfit), with
   * the label the book and Appearance show ("Season 1 champion"). They open no field of the book. 11. 10. 2026 (owner,
   * docs/thepit.md): the Pit's top 1% get the Foundry outfit in Epic, so not only Legendary.
   */
  rewards?: { set: SetId; rarity: 'common' | 'rare' | 'epic' | 'legendary'; label: string }[];
}

export const EMPTY_COLLECTION: CollectionState = { found: [], frames: [] };

const MVP_IDS = new Set<string>(MVP_ITEMS.map((i) => i.id));

export const COLLECTION_TOTAL = MVP_ITEMS.length * RARITIES.length;

/** Field key for an item, or null when the item is not part of the book (old removed templates). */
export function collectionKey(templateId: string, rarity: Rarity): string | null {
  const id = currentItemId(templateId);
  return MVP_IDS.has(id) ? `${id}:${rarity}` : null;
}

const CATEGORY_NAME: Record<WeaponClass | 'backpacks' | 'accessories', { name: string; title: string }> = {
  melee_1h: { name: 'One-handed melee', title: 'Bladesmith' },
  melee_2h: { name: 'Two-handed melee', title: 'Heavy Hitter' },
  ranged_1h: { name: 'One-handed ranged', title: 'Quick Draw' },
  ranged_2h: { name: 'Two-handed ranged', title: 'Long Shot' },
  backpacks: { name: 'Backpacks', title: 'Pack Rat' },
  accessories: { name: 'Accessories', title: 'Trinket Hoarder' },
};

const RARITY_TITLE: Partial<Record<Rarity, string>> = { epic: 'Curator', legendary: 'Living Legend' };

export const COLLECTION_GROUPS: CollectionGroup[] = [
  ...(['melee_1h', 'melee_2h', 'ranged_1h', 'ranged_2h'] as WeaponClass[]).map((c) => ({
    id: `cat_${c}`,
    kind: 'category' as const,
    name: CATEGORY_NAME[c].name,
    items: MVP_ITEMS.filter((i) => 'class' in i && i.class === c).map((i) => i.id),
    rarities: [...RARITIES],
    achievementId: `collection_cat_${c}`,
  })),
  { id: 'cat_backpacks', kind: 'category', name: CATEGORY_NAME.backpacks.name, items: MVP_ITEMS.filter((i) => i.slot === 'backpack').map((i) => i.id), rarities: [...RARITIES], achievementId: 'collection_cat_backpacks' },
  { id: 'cat_accessories', kind: 'category', name: CATEGORY_NAME.accessories.name, items: MVP_ITEMS.filter((i) => i.slot === 'accessory').map((i) => i.id), rarities: [...RARITIES], achievementId: 'collection_cat_accessories' },
  ...SET_IDS.map((s) => ({
    id: `set_${s}`,
    kind: 'set' as const,
    name: `${SETS[s].name} set`,
    items: MVP_ITEMS.filter((i) => 'setId' in i && i.setId === s).map((i) => i.id),
    rarities: [...RARITIES],
    achievementId: `collection_set_${s}`,
    frame: s,
  })),
  ...RARITIES.map((r) => ({
    id: `rarity_${r}`,
    kind: 'rarity' as const,
    name: `Every ${RARITY_NAME[r]}`,
    items: MVP_ITEMS.map((i) => i.id),
    rarities: [r],
    achievementId: `collection_rarity_${r}`,
  })),
];

/** Collection rewards as achievements (merged into ACHIEVEMENTS). */
export const COLLECTION_ACHIEVEMENTS: Record<string, AchievementDef> = Object.fromEntries(
  COLLECTION_GROUPS.map((g): [string, AchievementDef] => {
    if (g.kind === 'category') {
      const key = g.id.replace('cat_', '') as keyof typeof CATEGORY_NAME;
      return [g.achievementId, { id: g.achievementId, name: `${g.name}: complete`, description: `Find every ${g.name.toLowerCase()} item in every rarity.`, title: CATEGORY_NAME[key].title }];
    }
    if (g.kind === 'set') return [g.achievementId, { id: g.achievementId, name: `${g.name}: complete`, description: `Find every ${g.name} piece in every rarity. Unlocks its share card frame.` }];
    const r = g.rarities[0];
    return [g.achievementId, { id: g.achievementId, name: `${RARITY_NAME[r]} completionist`, description: `Find all ${MVP_ITEMS.length} items in ${RARITY_NAME[r]}.`, title: RARITY_TITLE[r], badge: true }];
  }),
);

export function groupKeys(g: CollectionGroup): string[] {
  return g.items.flatMap((id) => g.rarities.map((r) => `${id}:${r}`));
}

export function groupProgress(g: CollectionGroup, found: Set<string>): { found: number; total: number; done: boolean } {
  const keys = groupKeys(g);
  const n = keys.filter((k) => found.has(k)).length;
  return { found: n, total: keys.length, done: n === keys.length };
}

/** Adds found fields; returns the new state and whether anything changed. */
export function addFound(state: CollectionState | null | undefined, items: { templateId: string; rarity: Rarity }[]): { state: CollectionState; added: string[] } {
  const s = state ?? EMPTY_COLLECTION;
  const set = new Set(s.found);
  const added: string[] = [];
  for (const it of items) {
    const k = collectionKey(it.templateId, it.rarity);
    if (k && !set.has(k)) {
      set.add(k);
      added.push(k);
    }
  }
  return { state: added.length ? { ...s, found: [...set] } : s, added };
}

export function completedGroups(state: CollectionState): CollectionGroup[] {
  const found = new Set(state.found);
  return COLLECTION_GROUPS.filter((g) => groupProgress(g, found).done);
}

/** Found this item at least in Epic (Epic or Legendary field open): needed to craft its Legendary (6.6). */
export function foundAtLeastEpic(state: CollectionState | null | undefined, templateId: string): boolean {
  const f = new Set(state?.found ?? []);
  return f.has(`${templateId}:epic`) || f.has(`${templateId}:legendary`);
}
